import React from 'react';
import {
  FileWithExternalLink,
  LinkUploadFile,
  useBoolean,
  VStack,
} from '@wegroup/design-system';
import { useTranslation } from 'react-i18next';
import { InsuranceDetailFormValues } from '../../../../../types/Form';
import { Controller, useFormContext } from 'react-hook-form';
import AdvisoryReportTitleWithDescription from '../../../../shared/AdvisoryReportTitleWithDescription';
import useUpload from '../../../../../../../../../common/src/hooks/useUpload';
import useFilesFromDocumentAPI from '../../../../../../../../../common/src/api/getFilesFromBucketLink';
import InsuranceFormDocumentUpload from './InsuranceFormDocumentUpload';
import usePublicFiles, {
  getPublicFile,
} from '../../../../../../../../../common/src/api/getFilesFromPublicLink';
import { useQueryClient } from 'react-query';

const AddInsuranceModalInsuranceFormDocuments: React.FC = () => {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useBoolean();
  const [isUploadingLink, setIsUploadingLink] = useBoolean();

  const { control, watch, setValue } =
    useFormContext<InsuranceDetailFormValues>();
  const documents = watch('documents') || [];
  const links = watch('links') || [];

  const { uploadFile } = useUpload();
  const { data, isLoading } = useFilesFromDocumentAPI(documents);
  const results = usePublicFiles(links.map(({ link }) => link));
  const queryClient = useQueryClient();

  const handleAddFiles = async (files: File[]) => {
    setIsUploading.on();
    const docs = await Promise.all(
      files.map(async (file) => await uploadFile(file)),
    );
    setValue('documents', [...documents, ...docs], { shouldTouch: true });
    setIsUploading.off();
  };

  const handleAddLink = async (linkFile: LinkUploadFile) => {
    setIsUploadingLink.on();

    // Fetch file to check it's existence
    let data;
    try {
      data = await queryClient.fetchQuery({
        queryKey: linkFile.href,
        queryFn: () => getPublicFile(linkFile.href),
      });
    } catch (e) {
      setIsUploadingLink.off();
      throw new Error();
    }

    if (data) {
      // Set the value in the form
      setValue(
        'links',
        [
          ...links,
          {
            link: linkFile.href,
            filename: linkFile.filename,
          },
        ],
        {
          shouldTouch: true,
        },
      );
    }

    setIsUploadingLink.off();
  };

  const handleRemove = (index: number) => {
    const updatedDocuments = documents.filter((_, _index) => _index !== index);
    setValue('documents', updatedDocuments, { shouldTouch: true });
  };

  const handleRemoveLink = (index: number) => {
    const updatedLinks = links.filter((_, _index) => _index !== index);
    setValue('links', updatedLinks, { shouldTouch: true });
  };

  return (
    <VStack align="stretch" spacing="4">
      <AdvisoryReportTitleWithDescription
        title={t('ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DOCUMENTS_TITLE')}
        description={t(
          'ADVISORY-REPORTS_CURRENT-INSURANCE_MODAL_DOCUMENTS_DESCRIPTION',
        )}
      />
      <Controller
        control={control}
        name="documents"
        render={() => (
          <InsuranceFormDocumentUpload
            onAdd={handleAddFiles}
            onAddLink={handleAddLink}
            files={data || []}
            linkFiles={
              (results
                .map(({ data }) => data)
                .filter((data) => !!data) as FileWithExternalLink[]) || []
            }
            onRemove={handleRemove}
            onRemoveLink={handleRemoveLink}
            isLoading={isLoading || isUploading}
            isLoadingLinkFiles={
              results.some(({ isLoading }) => isLoading) || isUploadingLink
            }
          />
        )}
      />
    </VStack>
  );
};

export default AddInsuranceModalInsuranceFormDocuments;
